import { cn } from '../lib/cn.js';

export type SystemStatus = 'OK' | 'WAITING' | 'ALERT';

const TONE: Record<SystemStatus, { core: string; halo: string; label: string }> = {
  OK: { core: 'bg-accent', halo: 'bg-accent/30', label: 'Alt går som planlagt' },
  WAITING: { core: 'bg-warning', halo: 'bg-warning/30', label: 'Venter på mennesker' },
  ALERT: { core: 'bg-danger', halo: 'bg-danger/30', label: 'Noe krever handling' },
};

/**
 * Nova's pulsing core — the wordmark's status indicator (06-designsystem.md).
 * Cyan = all well, amber = waiting on humans, red = failure or overdue.
 */
export function NovaOrb({
  status = 'OK',
  size = 10,
  animate = true,
  className,
}: {
  status?: SystemStatus;
  size?: number;
  animate?: boolean;
  className?: string;
}) {
  const tone = TONE[status];
  return (
    <span
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size * 2, height: size * 2 }}
      title={tone.label}
      role="img"
      aria-label={`Nova-status: ${tone.label}`}
    >
      <span
        className={cn(
          'absolute rounded-full blur-[3px]',
          tone.halo,
          animate && 'animate-orb-pulse',
        )}
        style={{ width: size * 1.8, height: size * 1.8 }}
      />
      <span
        className={cn('relative rounded-full', tone.core)}
        style={{ width: size, height: size }}
      />
    </span>
  );
}

/** The NOVA wordmark with the orb as its status light. */
export function NovaWordmark({
  status = 'OK',
  compact = false,
}: {
  status?: SystemStatus;
  compact?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <NovaOrb status={status} size={compact ? 8 : 10} />
      {!compact && (
        <span className="font-display text-ink text-lg font-bold tracking-[0.22em]">NOVA</span>
      )}
    </span>
  );
}
